<template>
    <div class="app hue-page" :class="{ presenting }" :data-hue="selectedClass ? selectedClass.hue : null">
      <header class="bar no-print" v-if="!presenting">
        <router-link to="/" class="wordmark">Sitzplan</router-link>
        <nav class="bar-nav" aria-label="Verwalten">
          <button type="button" class="btn btn-sm" aria-label="Klassen" @click="toggleMenu('class')">
            <GraduationCap aria-hidden="true" /> <span class="bar-label">Klassen</span>
          </button>
          <button type="button" class="btn btn-sm" aria-label="Zimmer" @click="toggleMenu('room')">
            <LayoutGrid aria-hidden="true" /> <span class="bar-label">Zimmer</span>
          </button>
          <button type="button" class="btn btn-sm" aria-label="Daten" @click="toggleMenu('data')">
            <Save aria-hidden="true" /> <span class="bar-label">Daten</span>
          </button>
        </nav>
      </header>

      <main class="page">
        <section class="head">
          <div class="head-text">
            <h1 class="display">{{ selectedClass ? selectedClass.name : 'Sitzplan' }}</h1>
            <p class="meta" v-if="selectedClass && selectedRoom">
              <span>{{ roomLabel }}</span>
              <span>{{ studentsLabel }}</span>
              <span>{{ tablesLabel }}</span>
            </p>
            <p class="summary" v-if="selectedClass && selectedRoom">{{ status }}</p>
          </div>
          <div class="leaf controls no-print" v-if="selectedClass && selectedRoom && !presenting">
            <label class="field">
              <span class="field-label">Klasse</span>
              <select class="select" :value="selectedClass.id" @change="selectClass($event.target.value)">
                <option v-for="klasse in classes" :key="klasse.id" :value="klasse.id">{{ klasse.name }}</option>
              </select>
            </label>
            <label class="field">
              <span class="field-label">Zimmer</span>
              <select class="select" :value="selectedRoom.id" @change="selectRoom($event.target.value)">
                <option v-for="room in rooms" :key="room.id" :value="room.id">{{ room.name }}</option>
              </select>
            </label>
            <button type="button" class="btn" @click="editClassOpen = true">
              <Pencil aria-hidden="true" /> Klasse bearbeiten
            </button>
          </div>
        </section>

        <!-- Nothing to seat yet: say what's missing and offer the one next step -->
        <section class="leaf state-card" v-if="!selectedClass">
          <h2>Leg zuerst eine Klasse an</h2>
          <p>Gib ihr einen Namen, zum Beispiel 26Gx. Danach trägst du die Schüler:innen ein, oder du importierst gleich eine CSV-Datei mit Vor- und Nachnamen.</p>
          <form class="inline-form" @submit.prevent="addNewClass()">
            <input class="input" type="text" v-model.trim="newClassName" placeholder="Neue Klasse" aria-label="Name der neuen Klasse" maxlength="40" />
            <button type="submit" class="btn btn-primary" :disabled="newClassName.length === 0"><Plus aria-hidden="true" /> Klasse anlegen</button>
          </form>
          <button type="button" class="btn" @click="importModuleOpen = true"><Upload aria-hidden="true" /> Von CSV-Datei importieren</button>
        </section>
        <section class="leaf state-card" v-else-if="!selectedRoom">
          <h2>Jetzt fehlt noch ein Zimmer</h2>
          <p>Gib deinem Schulzimmer einen Namen, zum Beispiel 1.55. Danach klickst du die Felder an, auf denen Tische stehen.</p>
          <form class="inline-form" @submit.prevent="addNewRoom()">
            <input class="input" type="text" v-model.trim="newRoomName" placeholder="Neues Zimmer" aria-label="Name des neuen Zimmers" maxlength="40" />
            <button type="submit" class="btn btn-primary" :disabled="newRoomName.length === 0"><Plus aria-hidden="true" /> Zimmer anlegen</button>
          </form>
        </section>

        <Room v-else :config="selectedRoom" :selectedClass="selectedClass" :seats="seats" :pinned="pinned" :notice="notice"
          :canUndo="canUndo" :presenting="presenting" :hasFixedPlan="hasFixedPlan" :fixedActive="fixedActive"
          v-on:toggleTable="$event => toggleTable($event)"
          v-on:changeRows="$event => updateRoomRows($event)"
          v-on:changeCols="$event => updateRoomCols($event)"
          v-on:changeFront="$event => changeRoomFront($event)"
          v-on:generateOrder="$event => generateOrder($event)"
          v-on:moveSeat="$event => moveSeat($event)"
          v-on:togglePin="$event => togglePin($event)"
          v-on:clearPlan="$event => setSeats({}, [])"
          v-on:undo="$event => undo()"
          v-on:togglePresenting="$event => togglePresenting()"
          v-on:openGroupBuilder="$event => groupBuilderOpen = true"
          v-on:applyFixedPlan="$event => applyFixedPlan()"
          v-on:openFixedEditor="$event => fixedEditorOpen = true">
        </Room>
      </main>

      <SiteFooter v-if="!presenting" />

      <!-- Class Menu-->
      <BaseDialog :open="classMenuOpen" title="Meine Klassen" @close="classMenuOpen = false">
        <p class="hint" v-if="classes.length == 0">Noch keine Klassen. Gib unten den Namen einer Klasse ein.</p>
        <ul class="list class-list" v-else>
          <li class="option" v-for="klasse in classes" :key="klasse.id" :class="{active: selectedClass && klasse.id === selectedClass.id}" :data-hue="klasse.hue">
            <button type="button" class="option-label" @click="selectClass(klasse.id); classMenuOpen = false">
              <span class="swatch"></span>
              <span class="option-name">{{ klasse.name }}</span>
              <span class="option-meta">{{ countLabel(klasse.students.length, 'Schüler:in', 'Schüler:innen') }}</span>
              <span class="chip chip-ink" v-if="selectedClass && klasse.id === selectedClass.id">Ausgewählt</span>
            </button>
            <div class="option-actions" v-if="confirmDeleteId === klasse.id">
              <span class="option-confirm">Mit allen Sitzplänen löschen?</span>
              <button type="button" class="btn btn-sm" @click="deleteClass(klasse.id)">Löschen</button>
              <button type="button" class="btn btn-quiet btn-sm" @click="confirmDeleteId = null">Abbrechen</button>
            </div>
            <div class="option-actions" v-else>
              <button type="button" class="btn btn-quiet btn-sm btn-icon" :aria-label="`${klasse.name} bearbeiten`" @click="editClass(klasse.id)"><Pencil aria-hidden="true" /></button>
              <button type="button" class="btn btn-quiet btn-sm btn-icon" :aria-label="`${klasse.name} löschen`" @click="confirmDeleteId = klasse.id"><Trash2 aria-hidden="true" /></button>
            </div>
          </li>
        </ul>
        <form class="field add-form" @submit.prevent="addNewClass()">
          <label class="field-label" for="new-class">Neue Klasse</label>
          <div class="inline-form">
            <input id="new-class" class="input" type="text" v-model.trim="newClassName" placeholder="z. B. 26Gx" maxlength="40" />
            <button type="submit" class="btn btn-primary" :disabled="newClassName.length === 0"><Plus aria-hidden="true" /> Hinzufügen</button>
          </div>
        </form>
        <div class="actions">
          <button type="button" class="btn" @click="classMenuOpen = false; importModuleOpen = true"><Upload aria-hidden="true" /> Von CSV-Datei importieren</button>
        </div>
      </BaseDialog>

      <!-- Room Menu -->
      <BaseDialog :open="roomMenuOpen" title="Meine Schulzimmer" @close="roomMenuOpen = false; renamingRoomId = null">
        <p class="hint" v-if="rooms.length == 0">Noch keine Schulzimmer. Gib unten den Namen eines Zimmers ein, zum Beispiel 1.55.</p>
        <ul class="list room-list" v-else>
          <li class="option" v-for="room in rooms" :key="room.id" :class="{active: selectedRoom && room.id === selectedRoom.id}">
            <form class="inline-form option-rename" v-if="renamingRoomId === room.id" @submit.prevent="renameRoom(room)">
              <input class="input" type="text" v-model.trim="roomNameDraft" :aria-label="`Neuer Name für ${room.name}`" maxlength="40" />
              <button type="submit" class="btn btn-primary btn-sm" :disabled="roomNameDraft.length === 0">Speichern</button>
              <button type="button" class="btn btn-quiet btn-sm" @click="renamingRoomId = null">Abbrechen</button>
            </form>
            <template v-else>
              <button type="button" class="option-label" @click="selectRoom(room.id); roomMenuOpen = false">
                <span class="option-name">{{ room.name }}</span>
                <span class="option-meta">{{ countLabel(room.tables.filter(t => t.available).length, 'Tisch', 'Tische') }}</span>
                <span class="chip chip-ink" v-if="selectedRoom && room.id === selectedRoom.id">Ausgewählt</span>
              </button>
              <div class="option-actions" v-if="confirmDeleteId === room.id">
                <span class="option-confirm">Mit allen Sitzplänen löschen?</span>
                <button type="button" class="btn btn-sm" @click="deleteRoom(room.id)">Löschen</button>
                <button type="button" class="btn btn-quiet btn-sm" @click="confirmDeleteId = null">Abbrechen</button>
              </div>
              <div class="option-actions" v-else>
                <button type="button" class="btn btn-quiet btn-sm btn-icon" :aria-label="`${room.name} umbenennen`" @click="renamingRoomId = room.id; roomNameDraft = room.name"><Pencil aria-hidden="true" /></button>
                <button type="button" class="btn btn-quiet btn-sm btn-icon" :aria-label="`${room.name} kopieren`" title="Kopie mit denselben Tischen" @click="duplicateRoom(room)"><Copy aria-hidden="true" /></button>
                <button type="button" class="btn btn-quiet btn-sm btn-icon" :aria-label="`${room.name} löschen`" @click="confirmDeleteId = room.id"><Trash2 aria-hidden="true" /></button>
              </div>
            </template>
          </li>
        </ul>
        <form class="field add-form" @submit.prevent="addNewRoom()">
          <label class="field-label" for="new-room">Neues Zimmer</label>
          <div class="inline-form">
            <input id="new-room" class="input" type="text" v-model.trim="newRoomName" placeholder="z. B. 1.55" maxlength="40" />
            <button type="submit" class="btn btn-primary" :disabled="newRoomName.length === 0"><Plus aria-hidden="true" /> Hinzufügen</button>
          </div>
        </form>
      </BaseDialog>

      <!-- Data import Menu -->
      <BaseDialog :open="dataMenuOpen" title="Daten verwalten" @close="dataMenuOpen = false">
        <Export :classCount="classes.length" :roomCount="rooms.length" v-on:dataImported="$event => loadData()"></Export>
      </BaseDialog>

      <!-- Overlay Modules -->
      <Klasse v-if="selectedClass" :config="selectedClass" :open="editClassOpen"
        v-on:updateStudents="$event => updateStudents($event)"
        v-on:changeClassName="$event => changeClassName($event)"
        v-on:changeClassHue="$event => changeClassHue($event)"
        v-on:closeClassMenu="$event => closeClassMenu()">
      </Klasse>
      <ImportModule :open="importModuleOpen" v-on:closeImportModule="importModuleOpen=false" v-on:importNames="$event => addClassWithNames($event)"></ImportModule>
      <GroupBuilder v-if="selectedClass" :open="groupBuilderOpen" v-on:closeGroupBuilder="groupBuilderOpen=false" :config="selectedClass"></GroupBuilder>
      <FixedPlanEditor v-if="selectedClass && selectedRoom" :open="fixedEditorOpen" :config="selectedRoom" :selectedClass="selectedClass"
        :saved="fixedSeats" :current="seats"
        v-on:save="$event => saveFixedPlan($event)"
        v-on:remove="$event => removeFixedPlan()"
        v-on:close="$event => fixedEditorOpen = false">
      </FixedPlanEditor>
      <Onboarding v-if="showOnboarding" @complete="showOnboarding=false" />
      <ReleaseNotes
          v-if="showReleaseNotes && !showOnboarding && !presenting"
          :title="releaseNotes.title"
          :items="releaseNotes.items"
          @dismiss="dismissReleaseNotes" />
    </div>
  </template>

  <script>
  import { Copy, GraduationCap, LayoutGrid, Pencil, Plus, Save, Trash2, Upload } from '@lucide/vue';
  import { changeRoomSize, createClassFromName, createClassFromNameAndStudents, createRoomFromName, nextHue, plural, APP_VERSION } from './config.js';
  import { storeRooms, storeClasses, storePlans, storePins, storeFixedPlans, storeSelection, loadRooms, loadClasses, loadPlans, loadPins, loadFixedPlans, loadSelection, planKey, getLastSeenVersion, setLastSeenVersion } from './storage.js';
  import { generateSeating } from './seating.js';
  import Room from './components/room.vue';
  import Klasse from './components/klasse.vue';
  import ImportModule from './components/ImportModule.vue';
  import GroupBuilder from './components/GroupBuilder.vue'
  import FixedPlanEditor from './components/FixedPlanEditor.vue'
  import Export from './components/Export.vue'
  import Onboarding from './components/Onboarding.vue';
  import ReleaseNotes from './components/ReleaseNotes.vue';
  import BaseDialog from './components/ui/BaseDialog.vue';
  import SiteFooter from './components/ui/SiteFooter.vue';

  export default {
    name: 'Generator',
    data() {
      return  {
        rooms: [],
        classes: [],
        // Seating plans, one per class and room
        plans: {},
        // The students who keep their seat when a plan is shuffled, per class and room
        pins: {},
        // Arrangements built by hand and saved, one per class and room. Hand changes to the plan on show don't touch them.
        fixedPlans: {},
        // Earlier states of each plan, for "Rückgängig". Lasts until the page is closed.
        history: {},
        presenting: false,
        selectedClassId: null,
        selectedRoomId: null,
        newClassName: "",
        newRoomName: "",
        roomNameDraft: "",
        renamingRoomId: null,
        confirmDeleteId: null,
        notice: null,
        editClassOpen: false,
        importModuleOpen: false,
        groupBuilderOpen: false,
        fixedEditorOpen: false,
        classMenuOpen: false,
        roomMenuOpen: false,
        dataMenuOpen: false,
        showOnboarding: false,
        showReleaseNotes: false,
        releaseNotes: {
          title: 'Neu: feste Sitzordnungen',
          items: [
            'Du kannst jetzt pro Klasse und Zimmer eine feste Sitzordnung von Hand bauen und speichern: mit «Feste Ordnung erstellen» über dem Sitzplan.',
            'Danach wählst du jedes Mal selbst: «Zufällige Ordnung» mischt neu, «Feste Ordnung» setzt alle auf ihren gespeicherten Platz.',
            'Mischen oder Plätze tauschen verändert die gespeicherte Ordnung nicht.',
          ]
        }
      }
    },
    created () {
      this.loadData();
      try {
        const completed = localStorage.getItem('onboarding_completed') === '1';
        this.showOnboarding = !completed && this.classes.length === 0 && this.rooms.length === 0;
      } catch (e) {
        this.showOnboarding = false;
      }
      // Check for version updates
      try {
        const lastSeenVersion = getLastSeenVersion();
        const hasExistingData = this.classes.length > 0 || this.rooms.length > 0;

        if (lastSeenVersion && lastSeenVersion !== APP_VERSION) {
          // User has seen a different version - show release notes
          this.showReleaseNotes = true;
        } else if (lastSeenVersion === null) {
          // No version stored
          if (hasExistingData) {
            // Existing user without version stored - show release notes
            this.showReleaseNotes = true;
          } else {
            // Truly new user - set version silently
            setLastSeenVersion(APP_VERSION);
          }
        }
      } catch (e) {
        // Ignore errors
      }
    },
    mounted () {
      document.addEventListener('fullscreenchange', this.onFullscreenChange)
      window.addEventListener('keydown', this.onKeydown)
    },
    beforeUnmount () {
      document.removeEventListener('fullscreenchange', this.onFullscreenChange)
      window.removeEventListener('keydown', this.onKeydown)
      if (document.fullscreenElement) document.exitFullscreen()
    },
    components: {
      Room,
      Klasse,
      ImportModule,
      GroupBuilder,
      FixedPlanEditor,
      Export,
      Onboarding,
      ReleaseNotes,
      BaseDialog,
      SiteFooter,
      Copy, GraduationCap, LayoutGrid, Pencil, Plus, Save, Trash2, Upload,
    },
    computed: {
      selectedClass() {
        return this.classes.find(c => c.id === this.selectedClassId) || null
      },
      selectedRoom() {
        return this.rooms.find(r => r.id === this.selectedRoomId) || null
      },
      // Who sits where for this class in this room. Seats whose table or student is gone drop out.
      seats() {
        return this.validSeats(this.plans[this.currentKey])
      },
      fixedSeats() {
        return this.validSeats(this.fixedPlans[this.currentKey])
      },
      hasFixedPlan() {
        return Object.keys(this.fixedSeats).length > 0
      },
      fixedActive() {
        if (!this.hasFixedPlan || Object.keys(this.seats).length !== Object.keys(this.fixedSeats).length) return false
        return Object.keys(this.fixedSeats).every(index => this.seats[index] === this.fixedSeats[index])
      },
      currentKey() {
        return this.selectedClass && this.selectedRoom ? planKey(this.selectedClass.id, this.selectedRoom.id) : null
      },
      // Fixed seats only count while the student actually sits somewhere
      pinned() {
        const seated = Object.values(this.seats)
        return (this.pins[this.currentKey] || []).filter(studentId => seated.includes(studentId))
      },
      canUndo() {
        return (this.history[this.currentKey] || []).length > 0
      },
      tableCount() {
        return this.selectedRoom.tables.filter(t => t.available).length
      },
      roomLabel() {
        // "1.55" alone reads oddly; a name with letters already says what it is
        return /^[\d\s.,-]+$/.test(this.selectedRoom.name) ? `Zimmer ${this.selectedRoom.name}` : this.selectedRoom.name
      },
      studentsLabel() {
        return plural(this.selectedClass.students.length, 'Schüler:in', 'Schüler:innen')
      },
      tablesLabel() {
        return plural(this.tableCount, 'Tisch', 'Tische')
      },
      // The one sentence under the title that says where things stand
      status() {
        const students = this.selectedClass.students.length
        const tables = this.tableCount
        const seated = Object.keys(this.seats).length
        if (students === 0) return 'Diese Klasse hat noch keine Schüler:innen. Trag sie unter «Klasse bearbeiten» ein.'
        if (tables === 0) return `Klicke unten die Felder an, auf denen in deinem Zimmer Tische stehen: mindestens ${students}.`
        if (tables < students) return students - tables === 1 ? 'Es fehlt noch ein Tisch, damit alle einen Platz haben.' : `Es fehlen noch ${students - tables} Tische, damit alle einen Platz haben.`
        if (seated === 0) return this.hasFixedPlan ? 'Alles bereit: Mische neu, oder nimm die feste Ordnung.' : 'Alles bereit zum Mischen.'
        if (this.fixedActive) return seated < students ? `Feste Ordnung: ${seated} von ${students} haben einen Platz.` : 'Die feste Ordnung ist gesetzt.'
        if (seated < students) return `${seated} von ${students} haben einen Platz.`
        return students === 1 ? 'Der Platz ist vergeben.' : `Alle ${students} haben einen Platz.`
      },
    },
    methods: {
      countLabel: plural,
      validSeats: function(plan = {}) {
        const seats = {}
        if (!this.selectedClass || !this.selectedRoom) return seats
        const studentIds = this.selectedClass.students.map(s => s.id)
        this.selectedRoom.tables.forEach(table => {
          const studentId = plan[table.index]
          if (table.available && studentId && studentIds.includes(studentId)) seats[table.index] = studentId
        })
        return seats
      },
      toggleMenu: function(name) {
        this.confirmDeleteId = null
        this.dataMenuOpen = name === "data";
        this.classMenuOpen = name === "class";
        this.roomMenuOpen = name === "room";
      },
      dismissReleaseNotes: function() {
        setLastSeenVersion(APP_VERSION);
        this.showReleaseNotes = false;
      },
      saveClasses: function() {
        storeClasses(this.classes);
      },
      saveRooms: function() {
        storeRooms(this.rooms)
      },
      saveSelection: function() {
        storeSelection({ classId: this.selectedClassId, roomId: this.selectedRoomId })
      },
      // Keeps the selection if it still exists, otherwise falls back to the first class and room.
      assignDefaultSelections: function() {
        if (!this.selectedClass) {
          this.selectedClassId = this.classes.length > 0 ? this.classes[0].id : null
        }
        if (!this.selectedRoom) {
          this.selectedRoomId = this.rooms.length > 0 ? this.rooms[0].id : null
        }
        this.saveSelection()
      },
      loadData: function() {
        this.classes = loadClasses();
        this.rooms = loadRooms();
        this.plans = loadPlans();
        this.pins = loadPins();
        this.fixedPlans = loadFixedPlans();
        this.history = {}
        const selection = loadSelection();
        this.selectedClassId = this.selectedClassId || selection.classId || null
        this.selectedRoomId = this.selectedRoomId || selection.roomId || null
        this.notice = null
        this.assignDefaultSelections()
      },
      // Every change to a plan goes through here, so it can be taken back.
      setSeats: function(seats, pinned = this.pinned) {
        const key = this.currentKey
        const earlier = (this.history[key] || []).concat({ seats: { ...this.seats }, pinned: this.pinned.slice() })
        this.history = { ...this.history, [key]: earlier.slice(-20) }
        this.writePlan(key, seats, pinned)
      },
      writePlan: function(key, seats, pinned) {
        this.plans = { ...this.plans, [key]: seats }
        this.pins = { ...this.pins, [key]: pinned }
        this.notice = null
        storePlans(this.plans)
        storePins(this.pins)
      },
      undo: function() {
        const key = this.currentKey
        const earlier = (this.history[key] || []).slice()
        const last = earlier.pop()
        if (!last) return
        this.history = { ...this.history, [key]: earlier }
        this.writePlan(key, last.seats, last.pinned)
      },
      // Drops the seating plans of a class or room that no longer exists.
      removePlans: function(belongsToDeleted) {
        const keep = stored => {
          const kept = {}
          Object.keys(stored).forEach(key => {
            if (!belongsToDeleted(key)) kept[key] = stored[key]
          })
          return kept
        }
        this.plans = keep(this.plans)
        this.pins = keep(this.pins)
        this.fixedPlans = keep(this.fixedPlans)
        this.history = keep(this.history)
        storePlans(this.plans)
        storePins(this.pins)
        storeFixedPlans(this.fixedPlans)
      },
      generateOrder: function() {
        // Whoever has a fixed seat stays, everyone else is shuffled around them
        const fixed = {}
        Object.keys(this.seats).forEach(index => {
          if (this.pinned.includes(this.seats[index])) fixed[index] = this.seats[index]
        })
        const result = generateSeating(this.selectedClass.students, this.selectedRoom, fixed)
        if (!result) {
          this.notice = { kind: 'error', text: 'Es gibt mehr Schüler:innen als Tische. Setze weitere Tische, dann klappt es.' }
          return
        }
        this.setSeats(result.seats)
        const open = []
        if (result.separated > 0) {
          open.push(result.separated === 1 ? 'ein Paar aus einer Trenngruppe sitzt noch nebeneinander' : `${result.separated} Paare aus Trenngruppen sitzen noch nebeneinander`)
        }
        if (result.apart > 0) {
          open.push(result.apart === 1 ? 'jemand sitzt noch nicht bei der Partnergruppe' : `${result.apart} sitzen noch nicht bei ihrer Partnergruppe`)
        }
        if (open.length > 0) {
          const text = open.join(', und ')
          this.notice = { kind: 'note', text: `Nicht alles ging auf: ${text}. Mische nochmals, oder tausche die Plätze von Hand.` }
        }
      },
      // Everyone goes to their saved seat. Goes through setSeats, so it can be taken back like a shuffle.
      applyFixedPlan: function() {
        this.setSeats({ ...this.fixedSeats }, [])
        const seated = Object.values(this.fixedSeats)
        const missing = this.selectedClass.students.filter(s => !seated.includes(s.id)).map(s => s.name)
        if (missing.length > 0) {
          this.notice = { kind: 'note', text: `Noch ohne Platz in der festen Ordnung: ${missing.join(', ')}.` }
        }
      },
      saveFixedPlan: function(seats) {
        this.fixedPlans = { ...this.fixedPlans, [this.currentKey]: seats }
        storeFixedPlans(this.fixedPlans)
        this.fixedEditorOpen = false
      },
      removeFixedPlan: function() {
        const fixedPlans = { ...this.fixedPlans }
        delete fixedPlans[this.currentKey]
        this.fixedPlans = fixedPlans
        storeFixedPlans(this.fixedPlans)
        this.fixedEditorOpen = false
      },
      // A fixed seat survives the next shuffle. The student can still be moved by hand.
      togglePin: function(studentId) {
        const pinned = this.pinned.includes(studentId) ? this.pinned.filter(id => id !== studentId) : this.pinned.concat(studentId)
        this.setSeats({ ...this.seats }, pinned)
      },
      // Puts a student on another table; whoever sat there takes the old seat.
      moveSeat: function({ from, to }) {
        const seats = { ...this.seats }
        const moved = seats[from]
        if (seats[to]) {
          seats[from] = seats[to]
        } else {
          delete seats[from]
        }
        seats[to] = moved
        this.setSeats(seats)
      },
      toggleTable: function(index) {
        let table = this.selectedRoom.tables[index];
        table.available = !table.available;
        this.notice = null
        this.saveRooms()
      },
      resizeRoom: function(rows, cols) {
        const room = this.selectedRoom
        const moved = changeRoomSize(room, rows, cols)
        // The plans of every class in this room follow their tables
        const follow = stored => {
          const plans = {}
          Object.keys(stored).forEach(key => {
            if (!key.endsWith(`|${room.id}`)) {
              plans[key] = stored[key]
              return
            }
            plans[key] = {}
            Object.keys(stored[key]).forEach(index => {
              if (moved[index] !== undefined) plans[key][moved[index]] = stored[key][index]
            })
          })
          return plans
        }
        this.plans = follow(this.plans)
        this.fixedPlans = follow(this.fixedPlans)
        // Earlier plans point at tables that have moved
        this.history = {}
        storePlans(this.plans)
        storeFixedPlans(this.fixedPlans)
        this.saveRooms()
      },
      changeRoomFront: function(front) {
        this.selectedRoom.front = front
        this.saveRooms()
      },
      // The plan alone, as large as the screen allows. Leaving fullscreen ends it.
      togglePresenting: function() {
        this.presenting = !this.presenting
        const root = document.documentElement
        if (this.presenting && root.requestFullscreen) {
          root.requestFullscreen().catch(() => {})
        } else if (!this.presenting && document.fullscreenElement) {
          document.exitFullscreen()
        }
      },
      onFullscreenChange: function() {
        if (!document.fullscreenElement) this.presenting = false
      },
      // Where fullscreen isn't available, Escape still ends the presentation.
      onKeydown: function(event) {
        if (event.key !== 'Escape' || !this.presenting || document.fullscreenElement) return
        if (document.querySelector('dialog[open]')) return
        this.presenting = false
      },
      updateRoomRows: function(amount) {
        this.resizeRoom(this.selectedRoom.rows + amount, this.selectedRoom.cols)
      },
      updateRoomCols: function(amount) {
        this.resizeRoom(this.selectedRoom.rows, this.selectedRoom.cols + amount)
      },
      addClassWithNames: function(config) {
        this.importModuleOpen = false;
        this.addNewClassWithStudents(config.className, config.names);
      },
      updateStudents: function(students) {
        this.selectedClass.students = students;
        this.saveClasses()
      },
      changeClassName: function(name) {
        if(name.length === 0) return;
        this.selectedClass.name = name;
        this.saveClasses()
      },
      changeClassHue: function(hue) {
        this.selectedClass.hue = hue;
        this.saveClasses()
      },
      selectClass: function(id) {
        this.selectedClassId = id;
        this.notice = null
        this.saveSelection()
      },
      editClass: function(id) {
        this.selectClass(id)
        this.classMenuOpen = false;
        this.editClassOpen = true;
      },
      closeClassMenu: function() {
        this.editClassOpen = false;
      },
      selectRoom: function(id) {
        this.selectedRoomId = id
        this.notice = null
        this.saveSelection()
      },
      deleteClass: function(id) {
        this.classes = this.classes.filter(c => c.id !== id)
        this.removePlans(key => key.startsWith(`${id}|`))
        this.confirmDeleteId = null
        this.assignDefaultSelections()
        this.saveClasses()
      },
      deleteRoom: function(id) {
        this.rooms = this.rooms.filter(r => r.id !== id)
        this.removePlans(key => key.endsWith(`|${id}`))
        this.confirmDeleteId = null
        this.assignDefaultSelections()
        this.saveRooms()
      },
      // A new class opens straight away, so its students can go in.
      addNewClass: function() {
        if (this.newClassName.length  === 0) return;
        const klasse = createClassFromName(this.newClassName, nextHue(this.classes))
        this.classes.push(klasse)
        this.newClassName = ""
        this.saveClasses()
        this.editClass(klasse.id)
      },
      addNewClassWithStudents: function(name, students) {
        const klasse = createClassFromNameAndStudents(name, students, nextHue(this.classes))
        this.classes.push(klasse);
        this.saveClasses()
        this.selectClass(klasse.id)
      },
      addNewRoom: function() {
        if (this.newRoomName.length  === 0) return;
        const room = createRoomFromName(this.newRoomName)
        this.rooms.push(room)
        this.newRoomName = ""
        this.saveRooms()
        this.selectRoom(room.id)
        this.roomMenuOpen = false
      },
      renameRoom: function(room) {
        if (this.roomNameDraft.length === 0) return;
        room.name = this.roomNameDraft
        this.renamingRoomId = null
        this.saveRooms()
      },
      // The same tables under a new name, for a second room with the same layout.
      duplicateRoom: function(room) {
        const copy = createRoomFromName(`${room.name} (Kopie)`)
        copy.rows = room.rows
        copy.cols = room.cols
        copy.front = room.front
        copy.tables = room.tables.map(t => ({ index: t.index, available: t.available }))
        this.rooms.push(copy)
        this.saveRooms()
      },
    }
  }
  </script>

  <style scoped>
  .bar-nav {
    display: flex;
    gap: var(--s-2);
  }

  .head {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: var(--s-4) var(--s-6);
    padding: var(--s-4) 0 var(--s-5);
  }

  .head-text {
    display: grid;
    gap: var(--s-3);
    min-width: 0;
  }

  /* Class facts in a row, separated by ink dots */
  .meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--s-1) var(--s-3);
    font-weight: 700;
  }

  .meta span + span::before {
    content: "";
    display: inline-block;
    width: 5px;
    height: 5px;
    margin-right: var(--s-3);
    border-radius: 50%;
    background: var(--ink);
    vertical-align: middle;
  }

  .summary {
    max-width: 54ch;
    font-size: var(--t-xl);
    font-weight: 600;
    line-height: 1.35;
  }

  .controls {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 200px)) auto;
    align-items: end;
    gap: var(--s-3);
    padding: var(--s-4);
  }

  .list {
    display: grid;
    margin: 0;
    padding: 0;
    list-style: none;
    border-top: var(--line);
  }

  .list .option {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: var(--s-1) var(--s-2);
    min-height: 56px;
    padding: var(--s-1) 0;
    border-bottom: 1px solid var(--hairline);
  }

  .list .option.active {
    background: var(--hue-wash, var(--ground-wash));
  }

  .option-label {
    flex: 1 1 200px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--s-1) var(--s-3);
    min-height: var(--tap);
    min-width: 0;
    padding: var(--s-1) var(--s-2);
    border: 0;
    border-radius: var(--r-control);
    background: transparent;
    text-align: left;
    cursor: pointer;
    transition: background-color var(--dur-fast) var(--ease);
  }

  .option-label:hover {
    background: rgb(21 22 26 / 0.08);
  }

  .option-name {
    font-weight: 800;
    overflow-wrap: anywhere;
  }

  .option-meta {
    font-size: var(--t-sm);
    font-weight: 600;
    color: var(--ink-2);
  }

  .option-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: var(--s-1);
    padding-right: var(--s-1);
  }

  .option-confirm {
    margin-right: var(--s-1);
    font-size: var(--t-sm);
    font-weight: 700;
  }

  .option-rename {
    flex: 1;
    align-items: center;
    padding: 0 var(--s-1);
  }

  /* Presenting: the title in one line, then nothing but the room */
  .app.presenting {
    height: 100vh;
    height: 100dvh;
  }

  .presenting .page {
    display: flex;
    flex-direction: column;
    min-height: 0;
    padding-bottom: var(--gutter);
  }

  .presenting .head {
    padding: var(--s-4) 0 var(--s-3);
  }

  .presenting .head-text {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--s-1) var(--s-5);
  }

  .presenting .display {
    font-size: var(--t-3xl);
  }

  .presenting .summary {
    display: none;
  }

  .presenting .board {
    flex: 1;
  }

  @media (max-width: 899px) {
    .summary {
      font-size: var(--t-lg);
    }

    .controls {
      width: 100%;
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .controls .btn {
      grid-column: 1 / -1;
    }
  }

  @media (max-width: 599px) {
    .bar-label {
      display: none;
    }

    .bar-nav .btn {
      width: 36px;
      padding: 0;
    }
  }

  @media print {
    .head {
      padding: 0 0 var(--s-4);
    }

    .display {
      font-size: var(--t-3xl);
    }

    .summary {
      display: none;
    }
  }
  </style>
